import { decodeJWTTokenPayload, useAuth } from '@jutro/auth';
import React, {
    useEffect,
    useState,
    useContext
} from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { getConfigValue } from '@jutro/config';
import { getGrantedAuthorities, mockAuthContext, AuthContext as DigitalAuthContext } from '@xengage/gw-digital-auth-react';
import contextTransformer from './contextTransformer';
import { Loader } from '@jutro/components';
import { SecureRoute } from '@jutro/router';

const shouldCheckGrantedAuthorities = getConfigValue('GRANTED_AUTHORITIES_ENABLED', false);

const getGrantedAuthorityContext = async digitalAuthContext => {
    if (shouldCheckGrantedAuthorities) {
        const grantedAuthorities = await Promise.resolve(
            getGrantedAuthorities(digitalAuthContext)
        );

        return { grantedAuthorities };
    }

    return {};
};

const getAuthData = async (onAuthDataCreation, digitalAuthContext) => {
    if (onAuthDataCreation) {
        // application specific logic
        const authUserData = await onAuthDataCreation(digitalAuthContext);

        return { authUserData };
    }

    return {};
};

const getAuthState = async (jutroAuthInfo, onAuthDataCreation) => {
    if (
        !jutroAuthInfo ||
        !jutroAuthInfo.isAuthenticated ||
        !jutroAuthInfo.userInfo
    ) {
        return mockAuthContext;
    }

    const digitalAuthContext =
         contextTransformer.getDigitalAuthContext(jutroAuthInfo);
    const { accessToken } = digitalAuthContext;

    if (!accessToken) {
        // if we have no authHeader we'll have to rely on information
        // we already have about the token
        return digitalAuthContext;
    }

    const authData = await getAuthData(onAuthDataCreation, digitalAuthContext);
    const grantedAuthorities = await getGrantedAuthorityContext(
        digitalAuthContext
    );
    
    return { ...authData, ...grantedAuthorities, ...digitalAuthContext };
    //return Object.assign({}, authData, grantedAuthorities, digitalAuthContext);
};

/**
* Wrapper for the application content.
*
* This allows to access more easily the jutro context without having to inline multiple components.
*
* @memberof module:gw-digital-auth-react
* @function ContextWrapper
* @param {ReactElement} children - components to be wrapped with Authentication Context Provider
* @param {Function} onAuthDataCreation - function gets authorisation details
* @returns {ReactElement} children components wrapped by Cloud Authentication Context Provider
*/
function ContentWrapper({ children, onAuthDataCreation }) {
    const jutroAuthInfo = useAuth();
    const [digitalContextValue, setDigitalContextValue] = useState();
    
    useEffect(() => {
        getAuthState(jutroAuthInfo, onAuthDataCreation).then(
            setDigitalContextValue
        );
    }, [jutroAuthInfo, jutroAuthInfo.userInfo, onAuthDataCreation]);

    return (
        <DigitalAuthContext.Provider value={digitalContextValue}>
            <SecureRoute
                render={ () =>
                    jutroAuthInfo.isAuthenticated && !jutroAuthInfo.isPending ? (children) : (<Loader/>)
                }
            />
        </DigitalAuthContext.Provider>
    );
}

ContentWrapper.propTypes = {
    children: PropTypes.node.isRequired,
    onAuthDataCreation: PropTypes.func,
};

ContentWrapper.defaultProps = {
    onAuthDataCreation: _.noop,
};


/**
* Provides authorisation context for cloud (okta)
*
* @memberof module:gw-digital-auth-react
* @function CloudAuthenticationContext
* @param {ReactElement} children - components to be wrapped with Authentication Context Provider
* @param {Function} onAuthDataCreation - function gets authorisation details
* @returns {ReactElement} children components wrapped by Cloud Authentication Context Provider
*/
function CloudAuthenticationContext({ onAuthDataCreation, children }) {
    return (
        <ContentWrapper onAuthDataCreation={onAuthDataCreation}>
            {children}
        </ContentWrapper>
    );
}

CloudAuthenticationContext.propTypes = {
    children: PropTypes.node.isRequired,
    onAuthDataCreation: PropTypes.func
};

CloudAuthenticationContext.defaultProps = {
    onAuthDataCreation: undefined
};

export default CloudAuthenticationContext;
